import type {
  ChannelDescriptor,
  ElementDescriptor,
  ElementTopologyDescriptor,
  FixtureDescriptor,
  RigSnapshot,
} from './types';

/** The controller's channel key (`rig.ChannelKey`): `group/subType`, or `group` alone when there is no subType. */
export function channelKeyOf(channel: ChannelDescriptor): string {
  if (channel.key) return channel.key;
  const group = channel.group ?? '';
  return channel.subType ? `${group}/${channel.subType}` : group;
}

/** The first channel of each distinct key, in list order. */
export function distinctChannels(channels: readonly ChannelDescriptor[] | undefined): ChannelDescriptor[] {
  const seen = new Set<string>();
  return (channels ?? []).filter((channel) => {
    const key = channelKeyOf(channel);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * A fixture's own channel set: every distinct channel key across ALL of its elements, first-seen in element
 * order. The controller publishes a fixture node's whole-device `values` in exactly this order
 * (`fixtureChannelKeys` → `RuntimeDevice.StateFixtureChannelKeys`, de-duped `Descriptor.Channels`, which
 * walks every element), so every rig source sets `representativeChannels` from here — a fixture whose
 * elements differ (master + cells) has the union, never element 0's keys alone.
 */
export function fixtureChannels(
  elements: ReadonlyArray<{ channels?: readonly ChannelDescriptor[] }> | undefined,
): ChannelDescriptor[] {
  return distinctChannels((elements ?? []).flatMap((element) => element.channels ?? []));
}

function elementName(
  pattern: { prefix?: string; suffix?: string; start?: number; step?: number } | undefined,
  offset: number,
): string | undefined {
  if (!pattern) return undefined;
  const number = (pattern.start ?? 1) + offset * (pattern.step ?? 1);
  return `${pattern.prefix ?? ''}${number}${pattern.suffix ?? ''}`;
}

export function expandFixtureTopology(fixture: FixtureDescriptor): FixtureDescriptor {
  const topology = fixture.elementTopology;
  if (!topology || fixture.elements?.length) return fixture;
  if (!Number.isInteger(topology.count) || topology.count < 0) {
    throw new Error('elementTopology count must be a non-negative integer.');
  }
  const elements: Array<ElementDescriptor | undefined> = Array.from({ length: topology.count });
  const install = (index: number, channels: ChannelDescriptor[], name?: string): void => {
    if (index < 0 || index >= topology.count) {
      throw new Error(`elementTopology index ${index} is out of range.`);
    }
    if (elements[index]) throw new Error(`elementTopology index ${index} is covered more than once.`);
    elements[index] = { index, ...(name ? { name } : {}), channels };
  };
  for (const run of topology.runs) {
    if (!Number.isInteger(run.first) || !Number.isInteger(run.count) || !Number.isInteger(run.template)) {
      throw new Error('elementTopology run coordinates must be integers.');
    }
    const template = topology.channelTemplates[run.template];
    if (!template) throw new Error(`elementTopology run template ${run.template} is out of range.`);
    if (run.count <= 0 || run.first < 0 || run.first + run.count > topology.count) {
      throw new Error(`elementTopology run [${run.first},${run.first + run.count}) is out of range.`);
    }
    for (let offset = 0; offset < run.count; offset += 1) {
      install(run.first + offset, template.channels, elementName(run.namePattern, offset));
    }
  }
  for (const exception of topology.exceptions ?? []) {
    if (!Number.isInteger(exception.index) || exception.index < 0 || exception.index >= topology.count) {
      throw new Error(`elementTopology exception index ${exception.index} is out of range.`);
    }
    if (exception.channels != null) {
      install(exception.index, exception.channels, exception.name);
      continue;
    }
    const element = elements[exception.index];
    if (!element) throw new Error(`elementTopology name exception ${exception.index} is not covered by a run.`);
    if (exception.channelNames && exception.channelNames.length !== (element.channels?.length ?? 0)) {
      throw new Error(`elementTopology channel-name exception ${exception.index} has the wrong length.`);
    }
    elements[exception.index] = {
      ...element,
      ...(exception.name != null ? { name: exception.name } : {}),
      ...(exception.channelNames ? {
        channels: element.channels?.map((channel, index) => ({
          ...channel,
          name: exception.channelNames![index],
        })),
      } : {}),
    };
  }
  const expanded = elements.map((element, index) => {
    if (!element) throw new Error(`elementTopology index ${index} is not covered.`);
    return element;
  });
  const representative = fixtureChannels(expanded);
  if (fixture.elementCount != null && fixture.elementCount !== topology.count) {
    throw new Error(
      `elementTopology count ${topology.count} does not match fixture elementCount ${fixture.elementCount}.`,
    );
  }
  return {
    ...fixture,
    elements: expanded,
    elementCount: topology.count,
    representativeChannels: representative,
    isControllable: fixture.isControllable ?? true,
  };
}

interface CloudRigLayout {
  channels?: ChannelDescriptor[];
  elementTopology?: ElementTopologyDescriptor;
}

function hydrateFixtureLayout(
  fixture: FixtureDescriptor,
  layouts: Record<string, CloudRigLayout> | undefined,
): FixtureDescriptor {
  const profile = fixture.profile as { gdtfHash?: string; mode?: string } | undefined;
  const layout = profile?.gdtfHash && profile.mode ? layouts?.[`${profile.gdtfHash}::${profile.mode}`] : undefined;
  if (!layout) return fixture;
  if (!fixture.elementTopology && layout.elementTopology) {
    return { ...fixture, elementTopology: layout.elementTopology };
  }
  if (fixture.representativeChannels?.length || fixture.channels?.length || fixture.elementTopology) {
    return fixture;
  }
  const channels = distinctChannels(layout.channels);
  return channels.length ? { ...fixture, representativeChannels: channels } : fixture;
}

export function expandRigTopology(rig: RigSnapshot): RigSnapshot {
  return { ...rig, fixtures: rig.fixtures.map(expandFixtureTopology) };
}

/** Attach the platform's canonical GDTF layouts to cloud fixtures before capability-driven rendering. */
export function hydrateCloudRigLayouts(
  rig: RigSnapshot,
  layouts: Record<string, CloudRigLayout> | undefined,
): RigSnapshot {
  return expandRigTopology({
    ...rig,
    fixtures: rig.fixtures.map((fixture) => hydrateFixtureLayout(fixture, layouts)),
  });
}
